/**
 * reviewedDoc — the per-citation filter that applies human review decisions.
 *
 * The doc is the canonical record of AI output; `review_state` is the
 * canonical record of human decisions. They never blur. The live editor
 * view goes through `computeVisibleSources` (via the source-registry plugin)
 * to render superscripts and the bibliography against the reviewed view.
 *
 * The export pipeline does the same filtering server-side
 * (backend/core/export/review_filter.py) — kept symmetrical so a
 * server-triggered export with no open editor stays correct.
 */

// ── Types ───────────────────────────────────────────────────────────────────

/**
 * Per-claim hidden-source map.
 *   key   = claim_uuid (matches citation node `claimId` attr, set by backend
 *           tiptap_utils.py:404-406 from claim_row_id; matches frontend
 *           `snapshot.claimUuid` in ReviewPanelContext).
 *   value = set of source_uuids the human has hidden on that claim.
 */
export type HiddenByClaimUuid = Map<string, Set<string>>

/**
 * Citation node attribute shape (subset relevant to filtering).
 * Matches CitationNode.ts attrs spec.
 */
export interface CitationAttrs {
  claimId?: string | null
  attachmentId?: string | null
  sourceUuids?: string  // JSON array of UUIDs
  indices?: string
  claimIndices?: string | null
  sentenceHash?: string | null
}

export interface VisibleSourcesResult {
  /** Source UUIDs that survive the hidden-source filter, in original order. */
  visibleUuids: string[]
  /** True if every source on the citation is hidden. By D5 invariant this
   *  should never be true for a citation node still present in the doc. */
  allHidden: boolean
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function parseSourceUuids(raw: string | undefined | null): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.filter((u: unknown): u is string => typeof u === 'string' && u.length > 0)
    }
  } catch { /* fall through */ }
  return []
}

// ── Public: per-citation filter ─────────────────────────────────────────────

/**
 * Given a citation node's attrs and the global hidden-source map, return the
 * UUIDs that should be visible. Drives the source-registry plugin (live
 * editor superscripts + bibliography panel).
 */
export function computeVisibleSources(
  attrs: CitationAttrs,
  hiddenByClaimUuid: HiddenByClaimUuid,
): VisibleSourcesResult {
  const allUuids = parseSourceUuids(attrs.sourceUuids)
  if (allUuids.length === 0) {
    return { visibleUuids: [], allHidden: false }
  }

  const claimUuid = attrs.claimId
  const hiddenSet = claimUuid ? hiddenByClaimUuid.get(claimUuid) : undefined
  if (!hiddenSet || hiddenSet.size === 0) {
    return { visibleUuids: allUuids, allHidden: false }
  }

  const visibleUuids = allUuids.filter((u) => !hiddenSet.has(u))
  return {
    visibleUuids,
    allHidden: visibleUuids.length === 0,
  }
}
